import { useCallback, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { PAYMENT_CARD_DOTS_PATTERN } from '../settings/paymentCardDotsPattern';

const MAX_TILT_Y = 5;
const MAX_TILT_X = 6;

type GlareState = {
  x: number;
  y: number;
  angle: number;
  rotate: number;
};

const GLARE_IDLE: GlareState = { x: 50, y: 42, angle: 118, rotate: 0 };

type LightBloom = {
  tl: number;
  tr: number;
  br: number;
  bl: number;
  edgeTop: number;
  edgeRight: number;
  edgeBottom: number;
  edgeLeft: number;
  intensity: number;
};

const BLOOM_IDLE: LightBloom = {
  tl: 0.12,
  tr: 0.12,
  br: 0.1,
  bl: 0.1,
  edgeTop: 0.08,
  edgeRight: 0.08,
  edgeBottom: 0.08,
  edgeLeft: 0.08,
  intensity: 0.22,
};

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function lightBloomFromTilt(rotateX: number, rotateY: number, hovering: boolean): LightBloom {
  const k = 0.38;
  const base = hovering ? 0.42 : 0.22;
  const tiltBoost = (Math.abs(rotateX) + Math.abs(rotateY)) * 0.045;

  return {
    tl: clamp01(base + (-rotateY - rotateX) * k),
    tr: clamp01(base + (rotateY - rotateX) * k),
    br: clamp01(base + (rotateY + rotateX) * k),
    bl: clamp01(base + (-rotateY + rotateX) * k),
    edgeTop: clamp01(0.15 + Math.max(0, -rotateX) * k * 0.85),
    edgeRight: clamp01(0.15 + Math.max(0, rotateY) * k * 0.85),
    edgeBottom: clamp01(0.15 + Math.max(0, rotateX) * k * 0.85),
    edgeLeft: clamp01(0.15 + Math.max(0, -rotateY) * k * 0.85),
    intensity: clamp01(base + tiltBoost + (hovering ? 0.35 : 0)),
  };
}

type PaymentCardGlassShellProps = {
  children: ReactNode;
  className?: string;
  cardClassName?: string;
  maxTiltX?: number;
  maxTiltY?: number;
  whiteAnimatedBorder?: boolean;
};

export function PaymentCardGlassShell({
  children,
  className = '',
  cardClassName = '',
  maxTiltX = MAX_TILT_X,
  maxTiltY = MAX_TILT_Y,
  whiteAnimatedBorder = false,
}: PaymentCardGlassShellProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });
  const [glare, setGlare] = useState<GlareState>(GLARE_IDLE);
  const [bloom, setBloom] = useState<LightBloom>(BLOOM_IDLE);
  const [hovering, setHovering] = useState(false);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const el = cardRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      const rotateY = px * maxTiltX * 2;
      const rotateX = -py * maxTiltY * 2;
      setTilt({ rotateY, rotateX });
      setGlare({
        x: 50 + px * 42,
        y: 48 - py * 38,
        angle: 118 + px * 32 - py * 22,
        rotate: px * 5 - py * 3.5,
      });
      setBloom(lightBloomFromTilt(rotateX, rotateY, true));
    },
    [maxTiltX, maxTiltY],
  );

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
    setTilt({ rotateX: 0, rotateY: 0 });
    setGlare(GLARE_IDLE);
    setBloom(BLOOM_IDLE);
  }, []);

  const bloomStyle = {
    ['--payment-card-dots' as string]: PAYMENT_CARD_DOTS_PATTERN,
    ['--glare-x' as string]: `${glare.x}%`,
    ['--glare-y' as string]: `${glare.y}%`,
    ['--glare-angle' as string]: `${glare.angle}deg`,
    ['--glare-rotate' as string]: `${glare.rotate}deg`,
    ['--bloom-tl' as string]: bloom.tl,
    ['--bloom-tr' as string]: bloom.tr,
    ['--bloom-br' as string]: bloom.br,
    ['--bloom-bl' as string]: bloom.bl,
    ['--bloom-et' as string]: bloom.edgeTop,
    ['--bloom-er' as string]: bloom.edgeRight,
    ['--bloom-eb' as string]: bloom.edgeBottom,
    ['--bloom-el' as string]: bloom.edgeLeft,
    ['--bloom-intensity' as string]: bloom.intensity,
  } as CSSProperties;

  return (
    <div className={`payment-card-visual ${className}`.trim()}>
      <div
        ref={cardRef}
        className={`payment-card-glass payment-card-glass--tilt${hovering ? ' is-hovering' : ''} ${cardClassName}`.trim()}
        style={{
          transform: `perspective(920px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg)`,
          ...bloomStyle,
        }}
        onMouseEnter={() => {
          setHovering(true);
          setBloom((b) => ({ ...b, intensity: Math.min(1, b.intensity + 0.25) }));
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="payment-card-glass__warp" aria-hidden />
        <div className="payment-card-glass__grain" aria-hidden />
        <div className="payment-card-glass__dots" aria-hidden />
        <div className="payment-card-glass__dots-fine" aria-hidden />
        <div className="payment-card-glass__light-spread" aria-hidden />
        <div className="payment-card-glass__light-hot" aria-hidden />
        <div className="payment-card-glass__sheen" aria-hidden />
        <div className="payment-card-glass__scatter" aria-hidden />
        <div className="payment-card-glass__glare" aria-hidden />
        <div className="payment-card-glass__specular" aria-hidden />
        <div className="payment-card-glass__rim" aria-hidden />
        {whiteAnimatedBorder && (
          <div
            className="gift-card-animated-border absolute inset-0 z-[9] overflow-hidden rounded-[inherit] pointer-events-none"
            aria-hidden
          >
            <div className="aurora-mask-border gift-card-animated-border__mask absolute inset-0 rounded-[inherit]">
              <div className="gift-card-animated-border__rotation" />
            </div>
          </div>
        )}
        <div className="payment-card-glass__content">{children}</div>
      </div>
    </div>
  );
}
